import React, { useState } from 'react';

/**
 * FeedbackWidget Component
 * Provides thumbs up/down feedback functionality with optional comment section
 *
 * @param {string} contentId - Unique identifier for the content being rated
 * @param {string} storyId - Unique identifier for the story
 * @param {string} contentType - Type of content (e.g., 'timeline', 'case_presentation')
 * @param {string} contentSection - Specific section of the content (e.g., 'recommendation', 'summary')
 * @param {function} onSubmitFeedback - Callback function to submit feedback
 * @param {boolean} hasBeenSubmitted - Flag indicating if feedback has already been submitted
 */
const FeedbackWidget = ({ contentId, storyId, contentType, contentSection, onSubmitFeedback, hasBeenSubmitted }) => {
  const [feedback, setFeedback] = useState(null); // 'positive', 'negative', or null
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false); // Add this state

  // Use props to determine if feedback was already submitted
  const submitted = hasBeenSubmitted;

  const handleFeedback = (type) => {
    if (submitted || isSubmitting) return;

    setFeedback(type);
    if (type === 'negative') {
      setShowComment(true);
    } else {
      setShowComment(false);
    }
  };

  const handleSubmit = async () => {
    if (!feedback || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const feedbackData = {
        contentId,
        storyId,
        contentType,
        contentSection,
        rating: feedback,
        comment: comment.trim() || null,
        timestamp: new Date().toISOString()
      };

      await onSubmitFeedback(feedbackData);
      setIsSubmitted(true); // Set submitted state to true after successful submission
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Optionally show error message to user
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="feedback-widget submitted">
        <span className="text-green-600 text-sm">Merci pour votre retour !</span>
      </div>
    );
  }

  return (
    <div className="feedback-widget flex flex-col gap-2 mt-2 p-2 border-t border-gray-200">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Ce contenu était-il utile ?</span>
        <button
          onClick={() => handleFeedback('positive')}
          className={`p-1 rounded ${feedback === 'positive' ? 'bg-green-100' : ''}`}
          disabled={isSubmitting}
          aria-label="Thumbs up"
        >
          <span className="material-symbols-outlined text-green-600">thumb_up</span>
        </button>
        <button
          onClick={() => handleFeedback('negative')}
          className={`p-1 rounded ${feedback === 'negative' ? 'bg-red-100' : ''}`}
          disabled={isSubmitting}
          aria-label="Thumbs down"
        >
          <span className="material-symbols-outlined text-red-600">thumb_down</span>
        </button>
      </div>

      {feedback === 'positive' && !showComment && (
        <button
          onClick={() => setShowComment(true)}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Ajouter un commentaire
        </button>
      )}

      {showComment && (
        <div className="mt-2">
          <textarea
            className={`w-full p-2 border rounded text-sm ${
              isSubmitted ? 'bg-gray-50 text-gray-500' : 'text-gray-600'
            }`}
            placeholder="Votre commentaire..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isSubmitting || isSubmitted}
          />
        </div>
      )}

      {feedback && (
        <div className="mt-2">
          <button
            className={`${
              isSubmitted 
                ? 'bg-green-500 hover:bg-green-600' 
                : 'bg-blue-400 hover:bg-blue-500'
            } text-white text-sm py-1 px-3 rounded`}
            onClick={handleSubmit}
            disabled={isSubmitting || isSubmitted}
          >
            {isSubmitting ? 'Envoi...' : isSubmitted ? 'Envoyé' : 'Envoyer'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FeedbackWidget;